/**
 * API 클라이언트 래퍼
 * 기존 axios 인스턴스를 래핑하여 응답 언래핑, 에러 정규화 제공
 */

import type { AxiosRequestConfig } from 'axios';
import { api } from './axios';
import type { ApiMeta, ApiResponse } from '@/types/api';
import { ApiError, normalizeError } from './api-error';

// ============================================
// API Client Class
// ============================================

class ApiClient {
  /**
   * GET 요청 - 데이터와 메타데이터를 포함한 전체 응답 반환
   */
  async get<T, TMeta extends ApiMeta = ApiMeta>(
    url: string,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T, TMeta>> {
    try {
      const response = await api.get<ApiResponse<T, TMeta>>(url, config);

      if (!response.data.success) {
        throw new ApiError(response.data);
      }

      return response.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /**
   * POST 요청
   */
  async post<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.post<ApiResponse<T>>(url, data, config);

      if (!response.data.success) {
        throw new ApiError(response.data);
      }

      return response.data.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /**
   * POST 요청 - 전체 응답 반환
   */
  async postWithMeta<T, D = unknown, TMeta extends ApiMeta = ApiMeta>(
    url: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T, TMeta>> {
    try {
      const response = await api.post<ApiResponse<T, TMeta>>(url, data, config);

      if (!response.data.success) {
        throw new ApiError(response.data);
      }

      return response.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /**
   * PUT 요청
   */
  async put<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.put<ApiResponse<T>>(url, data, config);

      if (!response.data.success) {
        throw new ApiError(response.data);
      }

      return response.data.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /**
   * PATCH 요청
   */
  async patch<T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.patch<ApiResponse<T>>(url, data, config);

      if (!response.data.success) {
        throw new ApiError(response.data);
      }

      return response.data.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /**
   * DELETE 요청
   */
  async delete<T = void>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await api.delete<ApiResponse<T>>(url, config);

      if (!response.data.success) {
        throw new ApiError(response.data);
      }

      return response.data.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }
}

// ============================================
// Export Singleton Instance
// ============================================

export const apiClient = new ApiClient();
